"use client"

import { useQuery } from "@tanstack/react-query"
import { SearchNormal1 } from "iconsax-reactjs"
import * as React from "react"

import { getBankList } from "@/api"
import { GoBack } from "@/components/shared/layout"
import { Image } from "@/components/ui"
import { useLayoutStore } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import type { BankAccount } from "@/modules/types"

interface AutoCompleteBankProps {
  value?: BankAccount
  onChange: (value?: BankAccount) => void
}

export const AutoCompleteBank: React.FC<AutoCompleteBankProps> = ({ value, onChange }) => {
  const { insetBottom, insetTop } = useLayoutStore()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const { data: banks } = useQuery({
    queryKey: ["vietnam_banks_card"],
    queryFn: () => getBankList()
  })

  const bankVietNamAccounts = React.useMemo<BankAccount[]>(
    () => banks?.data?.data?.list || [],
    [banks]
  )

  const filtered = React.useMemo(() => {
    if (!search) return bankVietNamAccounts
    const q = search.toLowerCase()
    return bankVietNamAccounts.filter(
      (item) =>
        item?.bankName?.toLowerCase().includes(q) || item?.description?.toLowerCase().includes(q)
    )
  }, [search, bankVietNamAccounts])

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex w-full h-12 items-center text-white justify-between rounded-2xl px-3 py-2 text-left text-sm",
          {
            "bg-white text-black": !value?.thumbnail
          }
        )}
      >
        <span className="flex items-center gap-2 uppercase text-sm font-medium">
          {value?.thumbnail && (
            <Image
              src={value.thumbnail}
              alt={value.bankName}
              className="bg-gray-50 object-contain w-10 h-10 p-1 rounded-full"
            />
          )}
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{value?.bankName || "Chọn ngân hàng"}</p>
            {value?.description && (
              <p className="text-xss">{value?.description} </p>
            )}  
          </div>
        </span>
      </button>

      {/* Full screen overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-white flex flex-col"
          style={{
            paddingTop: insetTop,
            paddingBottom: insetBottom
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-0 flex-shrink-0">
            <GoBack
              title="Thêm tài khoản"
              onClick={() => {
                setOpen(false)
                setSearch("")
              }}
            />
          </div>

          {/* Search */}
          <div className="px-4 pb-4 border-b border-gray-200 flex-shrink-0">
            <div className="relative">
              <SearchNormal1
                color="#0091AA"
                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Tìm kiếm ngân hàng..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-4 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
            </div>
          </div>

          {/* Bank list */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {filtered.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                Không tìm thấy ngân hàng
              </div>
            ) : (
              <div>
                {filtered.map((bank) => (
                  <button
                    key={bank.id}
                    type="button"
                    onClick={() => {
                      onChange(bank)
                      setOpen(false)
                      setSearch("")
                    }}
                    className="flex w-full items-center gap-3 p-4 text-left hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100 last:border-b-0"
                  >
                    <Image
                      src={bank.thumbnail}
                      alt={bank.bankName}
                      className="bg-gray-50 object-contain w-10 h-10 p-1 rounded-sm"
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm font-medium">{bank.bankName}</span>
                      <span className="truncate text-xs text-gray-500">{bank.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
